import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { X, Upload, AlertTriangle, Check } from 'lucide-react';
import { adminApi } from '../../services/api';
import { ahoraLocal, esPasada, proximaOrientacion } from '../../services/orientacion';

// Alta masiva desde Excel o CSV, en dos pasos: se comprueba el archivo y se
// enseña fila a fila; después, con confirmación expresa, el servidor vuelve a
// validarlo y crea solo las filas válidas, con la bienvenida en cola.
//
// El archivo se lee entero al elegirlo y las dos peticiones envían esa misma
// copia en memoria: lo que se crea es exactamente lo que se revisó, aunque el
// archivo cambie en el disco entre los dos pasos. Para usar otro hay que
// volver al primer paso y elegirlo de nuevo.
//
// Cada importación corresponde a una orientación: su fecha y hora se eligen
// en el primer paso, se envían igual en los dos y se aplican a todas las filas.

const MAX_MB = 2;  // el mismo límite que el servidor

const ESTADO_FILA = {
  valida: ['Válida', 'success'], error: ['Error', 'danger'], aviso: ['Aviso', 'warning'],
};

const mensajeError = (err, porDefecto) => err.response?.data?.error || porDefecto;

const leerArchivo = (file) => (
  file.arrayBuffer
    ? file.arrayBuffer()
    : new Promise((ok, ko) => {
        const r = new FileReader();
        r.onload = () => ok(r.result);
        r.onerror = () => ko(r.error);
        r.readAsArrayBuffer(file);
      })
);

export default function ImportarAltas({ plantillaProvisional, onClose, onCreadas }) {
  const [archivo, setArchivo] = useState(null);     // copia en memoria
  const [fecha, setFecha] = useState(proximaOrientacion);
  const [leyendo, setLeyendo] = useState(false);
  const [revision, setRevision] = useState(null);   // respuesta de la comprobación
  const [creadas, setCreadas] = useState(null);     // respuesta de la creación
  const [revisado, setRevisado] = useState(false);
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  const elegirArchivo = async (file) => {
    setArchivo(null);
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`El archivo ocupa más de ${MAX_MB} MB.`);
      return;
    }
    setLeyendo(true);
    try {
      const contenido = await leerArchivo(file);
      if (!contenido || !contenido.byteLength) throw new Error('archivo vacío');
      setArchivo(new File([contenido], file.name, { type: file.type || 'application/octet-stream' }));
    } catch {
      toast.error('No se ha podido leer el archivo. Si está en una carpeta en la nube, '
        + 'descárgalo primero al equipo y vuelve a elegirlo.');
    }
    setLeyendo(false);
  };

  const comprobar = async () => {
    if (!archivo) { toast.error('Elige el archivo con las altas.'); return; }
    if (!fecha) { toast.error('Indica la fecha y hora de la orientación.'); return; }
    if (esPasada(fecha)) { toast.error('La fecha de la orientación ya ha pasado.'); return; }
    setTrabajando(true);
    try {
      const res = await adminApi.importarAltas(archivo, false, fecha);
      setRevision(res.data);
      setRevisado(false);
      setSoloProblemas(res.data.totales.errores + res.data.totales.avisos > 0);
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido comprobar el archivo.'));
    }
    setTrabajando(false);
  };

  const crear = async () => {
    setTrabajando(true);
    try {
      const res = await adminApi.importarAltas(archivo, true, fecha);
      setCreadas(res.data);
      onCreadas();
    } catch (err) {
      toast.error(mensajeError(err, 'No se han podido crear las altas.'));
      // El archivo es el mismo, pero los datos pueden haber cambiado entretanto
      // (por ejemplo, otra alta con el mismo correo): se enseña lo que ha visto
      // el servidor.
      if (err.response?.data?.filas) setRevision(err.response.data);
    }
    setTrabajando(false);
  };

  const volver = () => { setRevision(null); setArchivo(null); setRevisado(false); };

  const t = revision?.totales;
  const filas = (revision?.filas || []).filter(f => !soloProblemas || f.estado !== 'valida');

  return (
    <div className="modal-overlay" onClick={trabajando ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: revision && !creadas ? 900 : 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <h3>Importar altas</h3>
          <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={trabajando}><X size={18} /></button>
        </div>

        {/* Paso 1: el archivo */}
        {!revision && !creadas && (
          <>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.88rem', marginBottom: 12 }}>
              Sube un Excel (.xlsx) o un CSV con una fila por candidato y estas columnas en la primera
              fila: <strong>nombre</strong>, <strong>apellidos</strong>, <strong>código de
              restaurante</strong> y <strong>correo</strong>. Como máximo, 200 filas.
            </p>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.88rem', marginBottom: 16 }}>
              Primero se comprueba el archivo y verás el resultado fila a fila. No se crea nada hasta
              que lo confirmes.
            </p>
            <div className="form-group">
              <input className="form-input" type="file"
                accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                disabled={leyendo || trabajando}
                onChange={e => elegirArchivo(e.target.files[0] || null)} />
            </div>
            <div className="form-group">
              <label>Fecha y hora de la orientación *</label>
              <input className="form-input" type="datetime-local" value={fecha} min={ahoraLocal()}
                disabled={trabajando} onChange={e => setFecha(e.target.value)} />
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>
                Se aplica a todas las altas del archivo. Se propone el próximo martes a las 10:00.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={onClose} disabled={trabajando}>Cancelar</button>
              <button className="btn btn--primary" onClick={comprobar} disabled={trabajando || leyendo || !archivo}>
                <Upload size={14} /> {leyendo ? 'Leyendo...' : trabajando ? 'Comprobando...' : 'Comprobar archivo'}
              </button>
            </div>
          </>
        )}

        {/* Paso 2: revisión y confirmación */}
        {revision && !creadas && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, fontSize: '0.9rem' }}>
              <span className="badge badge--neutral">{t.total} filas</span>
              <span className="badge badge--success">{t.validas} válidas</span>
              {t.errores > 0 && <span className="badge badge--danger">{t.errores} con error</span>}
              {t.avisos > 0 && <span className="badge badge--warning">{t.avisos} con aviso</span>}
            </div>
            <p style={{ fontSize: '0.88rem', marginBottom: 10 }}>
              <strong>Orientación:</strong> {revision.orientacion_texto}
            </p>
            {(t.errores > 0 || t.avisos > 0) && (
              <p style={{ fontSize: '0.84rem', color: 'var(--gray-600)', marginBottom: 10 }}>
                Las filas con error o con aviso <strong>no se crearán</strong>. Corrígelas en el archivo
                y vuelve a importarlas, o crea esas altas una a una.
              </p>
            )}
            {revision.plantilla_provisional && (
              <div className="alert alert--warning" style={{ fontSize: '0.84rem', marginBottom: 10 }}>
                <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> La plantilla del correo de
                bienvenida es provisional: no se pueden importar altas hasta{' '}
                <Link to="/gestion/solicitudes/correo">revisarla</Link>.
              </div>
            )}
            <label style={{ fontSize: '0.84rem', display: 'block', marginBottom: 8 }}>
              <input type="checkbox" checked={soloProblemas} onChange={e => setSoloProblemas(e.target.checked)}
                style={{ marginRight: 6 }} />
              Ver solo las filas con error o aviso
            </label>
            <div style={{ maxHeight: '42vh', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
              <table className="data-table" style={{ fontSize: '0.82rem', margin: 0 }}>
                <thead>
                  <tr><th>Fila</th><th>Nombre</th><th>Restaurante</th><th>Correo</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {filas.map(f => {
                    const [texto, color] = ESTADO_FILA[f.estado];
                    return (
                      <tr key={f.fila} style={f.estado !== 'valida' ? { background: 'var(--gray-50)' } : undefined}>
                        <td>{f.fila}</td>
                        <td>{f.nombre} {f.apellidos}</td>
                        <td>{f.restaurante_nombre || f.restaurante}</td>
                        <td style={{ wordBreak: 'break-all' }}>{f.correo}</td>
                        <td>
                          <span className={`badge badge--${color}`}>{texto}</span>
                          {[...f.errores, ...f.avisos].map((m, i) => (
                            <div key={i} style={{ fontSize: '0.78rem', color: 'var(--gray-600)', marginTop: 2 }}>{m}</div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                  {filas.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-500)' }}>Ninguna fila con problemas.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {t.validas > 0 && !revision.plantilla_provisional && (
              <label style={{ fontSize: '0.88rem', display: 'block', marginTop: 14 }}>
                <input type="checkbox" checked={revisado} onChange={e => setRevisado(e.target.checked)}
                  style={{ marginRight: 8 }} />
                He revisado los nombres y las direcciones. Se crearán {t.validas}{' '}
                {t.validas === 1 ? 'alta' : 'altas'} para la orientación del {revision.orientacion_texto} y
                cada candidato recibirá su correo de bienvenida con el enlace y la contraseña.
              </label>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={volver} disabled={trabajando}>Elegir otro archivo</button>
              <button className="btn btn--primary" onClick={crear}
                disabled={trabajando || !revisado || t.validas === 0 || revision.plantilla_provisional}>
                {trabajando ? 'Creando...' : `Crear ${t.validas} ${t.validas === 1 ? 'alta' : 'altas'}`}
              </button>
            </div>
          </>
        )}

        {/* Resultado */}
        {creadas && (
          <>
            <div className="alert alert--success" style={{ fontSize: '0.9rem' }}>
              <Check size={14} style={{ verticalAlign: 'middle' }} /> Se han creado {creadas.creadas.length}{' '}
              {creadas.creadas.length === 1 ? 'alta' : 'altas'}.
            </div>
            <p style={{ fontSize: '0.86rem', color: 'var(--gray-600)', marginTop: 12 }}>
              Los correos de bienvenida quedan en cola y se envían en los próximos minutos. Su estado
              aparece en la columna «Correo» del listado.
              {creadas.totales.errores + creadas.totales.avisos > 0 &&
                ` Las ${creadas.totales.errores + creadas.totales.avisos} filas con error o aviso no se han creado.`}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--primary" onClick={onClose}>Cerrar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
